import React, { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useInstallationContext } from '../../context/installation';
import { useScroll } from '../../hooks/interaction/use-scroll';
import { hexToRgb } from '../../utils/helpers';
import { getPageHeaderStyle, getPageStyle } from '../../utils/theme';
import './index.scss';

type PageCommonProps = {
  children?: ReactNode;
  header?: ReactNode;
  centerContent?: boolean;
  className?: string;
  containerClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
  carouselContent?: boolean;
  whiteContent?: boolean;
  headerAccent?: boolean;
};

type CustomPageProps = PageCommonProps & {
  kind?: 'custom';
  isStickyHeader?: boolean;
};

type AbovePageProps = Omit<PageCommonProps, 'kind'> & {
  kind: 'above';
};

type PageProps = CustomPageProps | AbovePageProps;

export const PageContext = createContext({ isScrollable: false });

export const Page: React.FC<PageProps> = (props) => {
  const {
    children,
    header,
    centerContent = false,
    className,
    containerClassName,
    headerClassName,
    contentClassName,
    carouselContent = false,
    whiteContent = false,
    headerAccent = false,
  } = props;
  const ref = useRef<HTMLDivElement>(null);
  const { scrollDown, isScrollable } = useScroll(ref, 0);
  const [hasSubmitButton, setHasSubmitButton] = useState<boolean>(false);
  const [hasTwoFixedButtons, setHasTwoFixedButtons] = useState<boolean>(false);
  const { selectedInstallation } = useInstallationContext();
  const isStickyHeader = props.kind !== 'above' ? props.isStickyHeader : false;
  const above = props.kind === 'above';

  useEffect(() => {
    setHasSubmitButton(Boolean(ref?.current?.querySelector('#submit-button')));
    setHasTwoFixedButtons(Boolean(ref?.current?.querySelector('#fixed-two-buttons')));
  }, [ref.current, header, className, containerClassName, contentClassName, isStickyHeader, above]);

  const pageStyle = getPageStyle(className);
  const pageHeaderStyle = getPageHeaderStyle(className, isStickyHeader);

  return (
    <div className={classNames('page', { above, [className as string]: className })} style={pageStyle}>
      {isStickyHeader && (
        <div
          className={classNames('page__header m-t-0', {
            'page__header--sticky': isStickyHeader,
            'page__header--sticky-active': isStickyHeader && scrollDown,
            [headerClassName as string]: headerClassName,
          })}
          style={pageHeaderStyle}
        >
          {headerAccent && (
            <div
              className="header-accent"
              {...(selectedInstallation?.hexColor && headerAccent
                ? { style: { backgroundColor: `rgba(${hexToRgb(selectedInstallation.hexColor)},0.3)` } }
                : {})}
            />
          )}
          {header}
        </div>
      )}
      <div
        ref={ref}
        className={classNames('page__container', {
          'page__container--carousel': carouselContent,
          'page__container--white': whiteContent,
          [containerClassName as string]: containerClassName,
        })}
      >
        {!isStickyHeader && (
          <div
            className={classNames('page__header m-t-0', {
              [headerClassName as string]: headerClassName,
            })}
          >
            {header}
          </div>
        )}
        <div
          className={classNames('page__content', {
            'page__content--center': centerContent,
            [contentClassName as string]: contentClassName,
          })}
        >
          <PageContext.Provider value={{ isScrollable }}>
            {children}
            {hasSubmitButton && <div className="page__fixed-button-spacing" />}
            {hasTwoFixedButtons && <div className="page__fixed-two-buttons-spacing" />}
          </PageContext.Provider>
        </div>
      </div>
    </div>
  );
};
